import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import type { CSSProperties, ReactElement } from "react";
import { memo } from "react";
import { Icons } from "@/lib/icons";
import { radius, space } from "@/theme/foundation";

export type ChannelKind = "line" | "fb" | "ig" | "all";

interface ChannelGlyphProps {
  kind: ChannelKind;
  size?: number;
}

const CHANNEL_TONES: Record<Exclude<ChannelKind, "all">, string> = {
  line: "var(--line)",
  fb: "var(--fb)",
  ig: "linear-gradient(135deg,#f9ce34,#ee2a7b 50%,#6228d7)",
};

const FA_BY_KIND = {
  line: Icons.line,
  fb: Icons.facebook,
  ig: Icons.instagram,
} as const;

function plate(size: number, tone: string, child: ReactElement) {
  const style: CSSProperties = {
    width: size,
    height: size,
    borderRadius: "26%",
    background: tone,
    position: "relative",
    boxShadow: "inset 0 -2px 6px rgba(0,0,0,.08), inset 0 2px 4px rgba(255,255,255,.4)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flex: "none",
  };
  return <div style={style}>{child}</div>;
}

const ChannelGlyphImpl = ({ kind, size = 44 }: ChannelGlyphProps) => {
  if (kind === "all") {
    return (
      <div
        style={{
          width: size,
          height: size,
          borderRadius: "26%",
          background: "var(--canvas)",
          border: "1px solid var(--hairline)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flex: "none",
        }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: space[0.5],
          }}
        >
          {[0, 1, 2, 3].map((i) => (
            <div
              key={i}
              style={{
                width: 8,
                height: 8,
                borderRadius: radius.chip,
                background: "var(--ink)",
              }}
            />
          ))}
        </div>
      </div>
    );
  }

  return plate(
    size,
    CHANNEL_TONES[kind],
    <FontAwesomeIcon
      icon={FA_BY_KIND[kind]}
      color="var(--canvas)"
      style={{ width: size * 0.55, height: size * 0.55 }}
    />,
  );
};

export const ChannelGlyph = memo(ChannelGlyphImpl);

interface ChannelBadgeProps {
  kind: Exclude<ChannelKind, "all">;
  size?: number;
}

export function ChannelBadge({ kind, size = 18 }: ChannelBadgeProps) {
  return (
    <span
      style={{
        width: size,
        height: size,
        borderRadius: "50%",
        background: CHANNEL_TONES[kind],
        border: "2px solid var(--canvas)",
        display: "inline-block",
        flex: "none",
      }}
    />
  );
}
